import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Grid,
  Card,
  CardContent,
  CardActions,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Alert,
  useTheme,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Category,
  Palette,
  Build,
  Home,
} from '@mui/icons-material';

interface CategoryItem {
  id: string;
  name: string;
  displayName: string;
  description: string;
  type: 'image' | 'stl';
  color: string;
  icon: string;
  isActive: boolean;
  createdAt: string;
}

interface CategoryManagementProps {
  onNotification: (message: string, severity: 'success' | 'error' | 'info' | 'warning') => void;
}

const CategoryManagement: React.FC<CategoryManagementProps> = ({ onNotification }) => {
  const theme = useTheme();
  const [categories, setCategories] = useState<CategoryItem[]>([
    // Image categories
    {
      id: '1',
      name: 'portfolio',
      displayName: 'Portfolio',
      description: 'Showcase images for portfolio',
      type: 'image',
      color: 'primary',
      icon: 'portfolio',
      isActive: true,
      createdAt: '2024-01-15',
    },
    {
      id: '2',
      name: 'gallery',
      displayName: 'Gallery',
      description: 'General gallery images',
      type: 'image',
      color: 'secondary',
      icon: 'gallery',
      isActive: true,
      createdAt: '2024-01-15',
    },
    {
      id: '3',
      name: 'logo',
      displayName: 'Logo',
      description: 'Logo variations and branding',
      type: 'image',
      color: 'success',
      icon: 'logo',
      isActive: true,
      createdAt: '2024-01-15',
    },
    // STL categories
    {
      id: '4',
      name: 'home',
      displayName: 'Home Decor',
      description: 'Home decoration and furniture items',
      type: 'stl',
      color: 'primary',
      icon: 'home',
      isActive: true,
      createdAt: '2024-01-15',
    },
    {
      id: '5',
      name: 'gadgets',
      displayName: 'Gadgets',
      description: 'Electronic accessories and gadgets',
      type: 'stl',
      color: 'secondary',
      icon: 'gadgets',
      isActive: true,
      createdAt: '2024-01-15',
    },
    {
      id: '6',
      name: 'art',
      displayName: 'Art',
      description: 'Artistic and decorative pieces',
      type: 'stl',
      color: 'success',
      icon: 'art',
      isActive: true,
      createdAt: '2024-01-15',
    },
    {
      id: '7',
      name: 'tools',
      displayName: 'Tools',
      description: 'Functional tools and utilities',
      type: 'stl',
      color: 'warning',
      icon: 'tools',
      isActive: true,
      createdAt: '2024-01-15',
    },
  ]);
  
  const [editingCategory, setEditingCategory] = useState<CategoryItem | null>(null);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [categoryType, setCategoryType] = useState<'image' | 'stl'>('image');

  const colorOptions = [
    { value: 'primary', label: 'Primary' },
    { value: 'secondary', label: 'Secondary' },
    { value: 'success', label: 'Success' },
    { value: 'warning', label: 'Warning' },
    { value: 'error', label: 'Error' },
    { value: 'info', label: 'Info' },
  ];

  const iconOptions = [
    { value: 'portfolio', label: 'Portfolio', icon: <Category /> },
    { value: 'gallery', label: 'Gallery', icon: <Palette /> },
    { value: 'logo', label: 'Logo', icon: <Palette /> },
    { value: 'home', label: 'Home', icon: <Home /> },
    { value: 'gadgets', label: 'Gadgets', icon: <Build /> },
    { value: 'art', label: 'Art', icon: <Palette /> },
    { value: 'tools', label: 'Tools', icon: <Build /> },
  ];

  const handleAddCategory = () => {
    const newCategory: CategoryItem = {
      id: Date.now().toString(),
      name: '',
      displayName: '',
      description: '',
      type: categoryType,
      color: 'primary',
      icon: 'portfolio',
      isActive: true,
      createdAt: new Date().toISOString().split('T')[0],
    };
    setEditingCategory(newCategory);
    setShowAddDialog(true);
  };

  const handleEditCategory = (category: CategoryItem) => {
    setEditingCategory({ ...category });
    setShowAddDialog(true);
  };

  const handleSaveCategory = () => {
    if (!editingCategory) return;

    if (!editingCategory.name || !editingCategory.displayName) {
      onNotification('Please fill in name and display name', 'error');
      return;
    }

    // Check for duplicate names
    const existingCategory = categories.find(
      cat => cat.name === editingCategory.name && cat.id !== editingCategory.id
    );
    if (existingCategory) {
      onNotification('A category with this name already exists', 'error');
      return;
    }

    if (editingCategory.id === Date.now().toString()) {
      // New category
      setCategories(prev => [editingCategory, ...prev]);
      onNotification('Category added successfully!', 'success');
    } else {
      // Update existing category
      setCategories(prev => prev.map(cat => 
        cat.id === editingCategory.id ? editingCategory : cat
      ));
      onNotification('Category updated successfully!', 'success');
    }
    
    setEditingCategory(null);
    setShowAddDialog(false);
  };

  const handleDeleteCategory = (id: string) => {
    setCategories(prev => prev.filter(cat => cat.id !== id));
    onNotification('Category deleted successfully', 'success');
  };

  const handleToggleActive = (id: string) => {
    setCategories(prev => prev.map(cat => 
      cat.id === id ? { ...cat, isActive: !cat.isActive } : cat
    ));
  };

  const getIconComponent = (iconName: string) => {
    const iconOption = iconOptions.find(option => option.value === iconName);
    return iconOption ? iconOption.icon : <Category />;
  };

  const getColorValue = (color: string) => {
    return color as 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info';
  };

  const imageCategories = categories.filter(cat => cat.type === 'image');
  const stlCategories = categories.filter(cat => cat.type === 'stl');

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" component="h2" color="primary" sx={{ fontWeight: 600 }}>
          Category Management
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Type</InputLabel>
            <Select
              value={categoryType}
              onChange={(e) => setCategoryType(e.target.value as 'image' | 'stl')}
              label="Type"
            >
              <MenuItem value="image">Images</MenuItem>
              <MenuItem value="stl">STL Files</MenuItem>
            </Select>
          </FormControl>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleAddCategory}
          >
            Add {categoryType.toUpperCase()} Category
          </Button>
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* Image Categories */}
        <Grid item xs={12} md={6}>
          <Paper elevation={0} sx={{ p: 3, borderRadius: 4 }}>
            <Typography variant="h6" gutterBottom color="primary">
              Image Categories
            </Typography>
            <Grid container spacing={2}>
              {imageCategories.map((category) => (
                <Grid item xs={12} sm={6} key={category.id}>
                  <Card
                    sx={{
                      opacity: category.isActive ? 1 : 0.6,
                      border: category.isActive ? 'none' : `1px solid ${theme.palette.divider}`,
                    }}
                  >
                    <CardContent sx={{ pb: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        {getIconComponent(category.icon)}
                        <Typography variant="h6" sx={{ ml: 1, flexGrow: 1 }}>
                          {category.displayName}
                        </Typography>
                        <Chip
                          label={category.name}
                          color={getColorValue(category.color)}
                          size="small"
                        />
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        {category.description}
                      </Typography>
                    </CardContent>
                    <CardActions>
                      <IconButton
                        size="small"
                        onClick={() => handleEditCategory(category)}
                        color="secondary"
                      >
                        <Edit />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleToggleActive(category.id)}
                        color={category.isActive ? 'default' : 'success'}
                      >
                        {category.isActive ? '✓' : '○'}
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteCategory(category.id)}
                        color="error"
                      >
                        <Delete />
                      </IconButton>
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Paper>
        </Grid>

        {/* STL Categories */}
        <Grid item xs={12} md={6}>
          <Paper elevation={0} sx={{ p: 3, borderRadius: 4 }}>
            <Typography variant="h6" gutterBottom color="primary">
              STL Categories
            </Typography>
            <Grid container spacing={2}>
              {stlCategories.map((category) => (
                <Grid item xs={12} sm={6} key={category.id}>
                  <Card
                    sx={{
                      opacity: category.isActive ? 1 : 0.6,
                      border: category.isActive ? 'none' : `1px solid ${theme.palette.divider}`,
                    }}
                  >
                    <CardContent sx={{ pb: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        {getIconComponent(category.icon)}
                        <Typography variant="h6" sx={{ ml: 1, flexGrow: 1 }}>
                          {category.displayName}
                        </Typography>
                        <Chip
                          label={category.name}
                          color={getColorValue(category.color)}
                          size="small"
                        />
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        {category.description}
                      </Typography>
                    </CardContent>
                    <CardActions>
                      <IconButton
                        size="small"
                        onClick={() => handleEditCategory(category)}
                        color="secondary"
                      >
                        <Edit />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleToggleActive(category.id)}
                        color={category.isActive ? 'default' : 'success'}
                      >
                        {category.isActive ? '✓' : '○'}
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteCategory(category.id)}
                        color="error"
                      >
                        <Delete />
                      </IconButton>
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Paper>
        </Grid>
      </Grid>

      {/* Add/Edit Dialog */}
      <Dialog open={showAddDialog} onClose={() => setShowAddDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingCategory?.id === Date.now().toString() ? 'Add Category' : 'Edit Category'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'grid', gap: 3, mt: 2 }}>
            <TextField
              fullWidth
              label="Name (ID)"
              value={editingCategory?.name || ''}
              onChange={(e) => setEditingCategory(prev => prev ? { ...prev, name: e.target.value } : null)}
              helperText="Used internally (e.g., 'portfolio', 'home')"
              required
            />
            
            <TextField
              fullWidth
              label="Display Name"
              value={editingCategory?.displayName || ''}
              onChange={(e) => setEditingCategory(prev => prev ? { ...prev, displayName: e.target.value } : null)}
              helperText="Shown to users (e.g., 'Portfolio', 'Home Decor')"
              required
            />

            <TextField
              fullWidth
              label="Description"
              value={editingCategory?.description || ''}
              onChange={(e) => setEditingCategory(prev => prev ? { ...prev, description: e.target.value } : null)}
              multiline
              rows={2}
            />

            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControl fullWidth>
                <InputLabel>Color</InputLabel>
                <Select
                  value={editingCategory?.color || 'primary'}
                  onChange={(e) => setEditingCategory(prev => prev ? { ...prev, color: e.target.value } : null)}
                  label="Color"
                >
                  {colorOptions.map((color) => (
                    <MenuItem key={color.value} value={color.value}>
                      {color.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>Icon</InputLabel>
                <Select
                  value={editingCategory?.icon || 'portfolio'}
                  onChange={(e) => setEditingCategory(prev => prev ? { ...prev, icon: e.target.value } : null)}
                  label="Icon"
                >
                  {iconOptions.map((icon) => (
                    <MenuItem key={icon.value} value={icon.value}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {icon.icon}
                        <Typography sx={{ ml: 1 }}>{icon.label}</Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <input
                type="checkbox"
                checked={editingCategory?.isActive || false}
                onChange={(e) => setEditingCategory(prev => prev ? { ...prev, isActive: e.target.checked } : null)}
              />
              <Typography sx={{ ml: 1 }}>
                Active (visible to users)
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAddDialog(false)}>Cancel</Button>
          <Button onClick={handleSaveCategory} variant="contained">
            {editingCategory?.id === Date.now().toString() ? 'Add Category' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CategoryManagement;
